import { useCallback, useEffect, useRef, useState } from 'react';
import { useCart, useCurrency } from '@backpackjs/storefront';
import { useRouter } from 'next/router';

import { mapLineItem } from './utils';

const IGNORED_PATHS = ['account', 'cart', 'collections', 'products'];
const LOGGED_OUT_ACCOUNT_PATHS = [
  '/account/login',
  '/account/register',
  '/account/reset',
  '/account/activate',
];

export function useDataLayerCustomer({ DEBUG, userProperties }) {
  const asPathRef = useRef(null);
  const cart = useCart();
  const currencyCode = useCurrency();
  const router = useRouter();
  const asPath = router.asPath.split('?')[0];

  const [delayForContextIsFinished, setDelayForContextIsFinished] =
    useState(false);
  const [userDataEventTriggered, setUserDataEventTriggered] = useState(false);

  const userDataEvent = useCallback(
    ({ currencyCode: passedCurrencyCode, userProperties: _userProperties }) => {
      const previousPath = sessionStorage.getItem('PREVIOUS_PATH');
      const list =
        (window.location.pathname.startsWith('/collections') &&
          window.location.pathname) ||
        (previousPath?.startsWith('/collections') && previousPath) ||
        '';
      const event = {
        event: 'dl_user_data',
        cart_total: cart?.estimatedCost?.totalAmount?.amount || '0.0',
        user_properties: _userProperties,
        ecommerce: {
          currencyCode:
            passedCurrencyCode ||
            currencyCode ||
            cart?.estimatedCost?.totalAmount?.currencyCode,
          cart_contents: {
            products: cart?.lines?.map(mapLineItem(list)) || [],
          },
        },
      };

      window.ElevarDataLayer = window.ElevarDataLayer ?? [];
      window.ElevarDataLayer.push(event);
      setUserDataEventTriggered(true);
      setDelayForContextIsFinished(false);
      if (DEBUG) console.log(`DataLayer:elevar:${event.event}`, event);
    },
    [cart?.updatedAt, currencyCode]
  );

  // Force delay on user data event to allow for elevar context to be ready
  useEffect(() => {
    setTimeout(() => setDelayForContextIsFinished(true), 1000);
  }, [asPath]);

  // Trigger 'dl_user_data' event on path change after base data is ready,
  // excluding account and paths that trigger event directly before their respective events
  useEffect(() => {
    if (
      !delayForContextIsFinished ||
      !userProperties ||
      IGNORED_PATHS.includes(asPath.split('/')[1]) ||
      asPath.startsWith('/search') ||
      asPath === asPathRef.current
    )
      return undefined;

    userDataEvent({ userProperties });

    asPathRef.current = asPath;
    return () => {
      asPathRef.current = null;
    };
  }, [asPath, delayForContextIsFinished, !!userProperties]);

  // Trigger 'dl_user_data' event on account pages after base data is ready,
  // excluding after login/register events
  useEffect(() => {
    if (
      !delayForContextIsFinished ||
      !userProperties ||
      !asPath.startsWith('/account') ||
      asPath === asPathRef.current
    )
      return undefined;

    const prevPath = sessionStorage.getItem('PREVIOUS_PATH');
    const currentPath = sessionStorage.getItem('CURRENT_PATH');
    const prevPathInlcRefresh = sessionStorage.getItem(
      'PREVIOUS_PATH_INLC_REFRESH'
    );

    // On login or register, prevent event as it triggers directly from the login/register events
    const isFromLoginOrRegisterEvent =
      (prevPath.startsWith('/account/login') ||
        prevPath.startsWith('/account/register')) &&
      currentPath !== prevPathInlcRefresh;
    const isLoggedOutAccountPath = LOGGED_OUT_ACCOUNT_PATHS.some((path) =>
      asPath.startsWith(path)
    );
    if (isFromLoginOrRegisterEvent && !isLoggedOutAccountPath) return undefined;

    // On log out, trigger event with guest visitor type
    const isFromLogoutEvent =
      asPath.startsWith('/account/login') && prevPath.startsWith('/account');
    const prevPathIsLoggedOutAccountPath = LOGGED_OUT_ACCOUNT_PATHS.some(
      (path) => prevPath.startsWith(path)
    );
    if (isFromLogoutEvent && !prevPathIsLoggedOutAccountPath) {
      userDataEvent({
        userProperties: { visitor_type: 'guest', user_consent: '' },
      });
    } else {
      // Else trigger event for all other instances
      userDataEvent({ userProperties });
    }

    asPathRef.current = asPath;
    return () => {
      asPathRef.current = null;
    };
  }, [asPath, delayForContextIsFinished, !!userProperties]);

  return { userDataEvent, userDataEventTriggered };
}
